import { Environment } from "@workspace/access-button-launcher/dist/types";
import { dmnIdentity } from "./dmnIdentity";
import demo from "./environments/demo.json";
import dev from "./environments/dev.json";
import preProd from "./environments/preProd.json";
import production from "./environments/production.json";
import staging from "./environments/staging.json";
import { parseToken } from "./helpers";

export type GetEnvironmentPropsResponse = {
  ENVIRONMENT: string;
  SHOW_VERSION_NUMBER: boolean;
  APP_BASE_URL: string;
  BASE_DMN_URL: string;
  BASE_BOOKINGS_URL: string;
  NEW_CHECKOUT_URL: string;
  WORKSPACE_GRAPHQL_API: string;
  COLLINS_API_BASE_URL: string;
  CORE_API_BASE_URL: string;
  AUTH_API_BASE_URL: string;
  WEB_SOCKET_URL: string | null;
  BROWSER_SIGN_IN_URL: string;
  BROWSER_SIGN_OUT_URL: string;
  IS_ACCESS_BUTTON_ENABLED: boolean;
  ACCESS_BUTTON_MENU_ITEMS: string;
  REACT_APP_CLARITY_TOKEN: string;
  ACCESS_BTN_ENV: Environment;
  BASE_AUTH_URL: string;
  INTERCOM_APP_ID: string;
  FEATURE_SENTIMENT_API_HOST?: string;
  FEATURE_SENTIMENT_ACCOUNT_KEY?: string;
  FEATURE_SENTIMENT_REGISTRY_URL?: string;
};

export const isLocalDev = () => {
  return process.env.DEV === "TRUE";
};

export const getEnvironmentProps = (
  token: string | undefined | null
): GetEnvironmentPropsResponse => {
  if (isLocalDev() && process.env.APP_ENV !== "preprod") {
    return dev as GetEnvironmentPropsResponse;
  }

  if (isLocalDev() && process.env.APP_ENV === "preprod" && !token) {
    return preProd as GetEnvironmentPropsResponse;
  }

  const environment = isDmnURL() ? getEnvFromUrl() : getEnvFromToken(token);

  switch (environment?.toLowerCase()) {
    case "preprod":
      return preProd as GetEnvironmentPropsResponse;
    case "staging":
      return staging as GetEnvironmentPropsResponse;
    case "demo":
      return demo as GetEnvironmentPropsResponse;
    default:
      return production as GetEnvironmentPropsResponse;
  }
};

export const getEnvFromToken = (
  token: string | undefined | null
): string | null => {
  if (!token) return null;
  const decodedToken = parseToken(token);
  const issuer = decodedToken?.iss;
  if (!issuer) return null;
  const idx = issuer.indexOf("identity");
  if (idx === -1) return null;
  return issuer.substring(8, idx);
};

export const getEnvFromUrl = (href: string = location.href) => {
  return (
    href.split(".")?.[1]?.replace("dmn", "") ??
    href.split(".")?.[1]?.replace("designmynight", "")
  );
};

export const isBrowser = () => !window.ReactNativeWebView;

export const isNativeTab = () => window.location !== window.parent.location;

export const isDmnURL = (href: string = location.href) =>
  href.includes("dmn") || href.includes("designmynight");

export const getEnvironment = async (): Promise<
  GetEnvironmentPropsResponse | undefined
> => {
  const token = await dmnIdentity.getAccessToken();
  return getEnvironmentProps(token);
};
